import React, { useCallback, useEffect, createContext, useState } from 'react';
import { getRoutesFilter } from 'utils/filters';
import * as http from 'utils/http';
import config from 'config';
import serialize from 'utils/serialize';

const getContracts = async (query) =>
  http.get(`${config.API_URL}mercury/dedicated_contracts/?${query}`);

const getJourneysByDriver = async (query) =>
  http.get(`${config.API_URL}assignment/journeys/by_driver/?${query}`);

const DedicatedContractsContext = createContext();

function DedicatedContractsContextProvider({ children }) {
  const [mounted, setMounted] = useState(true);

  const [loading, setLoading] = useState(false);
  const [fetchingJourneys, setFetchingJourneys] = useState(false);

  const [error, setError] = useState(null);

  const [dedicatedContracts, setDedicatedContracts] = useState([]);
  const [dedicatedContractsObject, setDedicatedContractsObject] = useState({});
  const [dedicatedContractsDrivers, setDedicatedContractsDrivers] = useState('');
  const [journeysByDriver, setJourneysByDriver] = useState({});

  const [filters, setFilters] = useState(null);

  const serializedFilters = serialize(filters || {});

  const dateFilter = (filters || {}).active_at;

  const fetchDedicatedContracts = useCallback((ownFilters) => {
    const { routes, date } = ownFilters;

    const filters = {
      active_at: date,
      fields: 'id,carrier,driver,vehicle,cost_per_day',
      page_size: 0,
      ...getRoutesFilter(routes),
    };

    setFilters(filters);
  }, []);

  const refreshJourneysByDriver = useCallback(
    async (driverId) => {
      const query = serialize({
        pickup_time_from: dateFilter,
        drivers: [driverId],
      });

      const data = await getJourneysByDriver(query);

      setJourneysByDriver({ ...journeysByDriver, ...data });
    },
    [dateFilter, journeysByDriver]
  );

  /** Effects */

  useEffect(() => {
    const contractsObject = dedicatedContracts.reduce(
      (acc, contract) => ({ ...acc, [contract.id]: contract }),
      {}
    );
    setDedicatedContractsObject(contractsObject);
  }, [dedicatedContracts]);

  useEffect(() => {
    const contractsDrivers = dedicatedContracts.map(({ driver }) => driver.id).join(',');
    setDedicatedContractsDrivers(contractsDrivers);
  }, [dedicatedContracts]);

  useEffect(() => {
    async function _fetchContracts(filters) {
      if (!mounted || !filters) {
        return;
      }

      setLoading(true);

      try {
        const data = await getContracts(filters);

        mounted && setDedicatedContracts(data);
      } catch (err) {
        setError(err);
      } finally {
        mounted && setLoading(false);
      }
    }

    _fetchContracts(serializedFilters);
  }, [mounted, serializedFilters]);

  useEffect(() => {
    async function _fetchJourneys(dateFilter, drivers) {
      if (!mounted || !dateFilter || !drivers) {
        return;
      }

      setFetchingJourneys(true);

      const filters = serialize({
        pickup_time_from: dateFilter,
        drivers,
      });

      try {
        const journeys = await getJourneysByDriver(filters);

        mounted && setJourneysByDriver(journeys);
      } catch (err) {
      } finally {
        mounted && setFetchingJourneys(false);
      }
    }

    _fetchJourneys(dateFilter, dedicatedContractsDrivers);
  }, [mounted, dateFilter, dedicatedContractsDrivers]);

  useEffect(() => {
    return () => setMounted(false);
  }, []);

  return (
    <DedicatedContractsContext.Provider
      value={{
        dedicatedContracts,
        dedicatedContractsObject,
        fetchDedicatedContracts,
        journeysByDriver,
        refreshJourneysByDriver,
        fetchingJourneys,
        loading,
        error,
      }}
    >
      {children}
    </DedicatedContractsContext.Provider>
  );
}

export { DedicatedContractsContextProvider };
export default DedicatedContractsContext;
